import classNames from 'classnames';
// @ts-expect-error typedef version skew; Context defs not yet implemented
import {Context, Table} from 'fixed-data-table-2';
import React, {useEffect} from 'react';
import _ from 'underscore';

import {StandardFeature} from 'auth/capabilities/CapabilityConstants';
import {FeatureStatus, getFeatureStatus} from 'auth/feature-enablement/FeatureStatus';
import DataConnector from 'connect/DataConnector';
import {REGISTRIES_FINDER_ID} from 'constants/NavConstants';
import * as NavConstants from 'constants/NavConstants';
import {BioentityAliasInfo} from 'constants/RegistryConstants';
import {BioentitySummary} from 'folder-items/FileConstants';
import * as FileUtil from 'folder-items/FileUtil';
import InventoryFileStore from 'inventory/InventoryFileStore';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import FolderItemListingRenderer, {
  getIdColumnRender,
  getNameColumnRender,
} from 'nav/expanded/FolderItemListingRenderer';
import {
  ColumnAccessorKey,
  ColumnConfiguration,
  ListingRendererType,
  ListingTypes,
} from 'nav/expanded/ListingRendererConstants';
import preferences from 'Preferences';
import {getStore} from 'redux-stores/Store';
import AssayStore from 'results/AssayStore';
import SampleSchemaStore from 'samples/SampleSchemaStore';
import StorableStore from 'samples/StorableStore';
import {HitHsp} from 'search/blast/alignmentTypes';
import {
  adjustScrollForZoomChange,
  AlignmentSelect,
  arrangeStartAndEnd,
  setEndSelect,
  setStartSelect,
} from 'search/blast/BLASTAlignmentSelectHelper';
import {
  ALIGNMENT_TABLE_SCROLLBAR_BUFFER,
  boundMinMax,
  getAdjustedX,
  getAdjustedY,
  getNormalRowHeight,
  getQueryRowHeight,
  getTotalComponentSize,
  insideAlignmentColumn,
  PAIRWISE_ALIGNMENT_ZOOM_THRESHOLD,
  QUERY_ROW_CONTENT_HEIGHT,
  roundToNearestMultiple,
  SEQUENCE_COLUMN_PADDING,
  zoomToPairwiseItemSize,
} from 'search/blast/BLASTAlignmentUtil';
import {ColoringSchemeType} from 'search/blast/ColoringScheme';
import GraphAlignment from 'search/blast/GraphAlignment';
import PairwiseAlignment from 'search/blast/PairwiseAlignment';
import ScrollbarAndSelectHandler from 'search/blast/ScrollbarAndSelectHandler';
import SequenceRangeCell from 'search/blast/SequenceRangeCell';
import {ListableItem, ListableItemExtras, Operator, Sort, SortKey} from 'search/SearchConstants';
import {getColumnHeaderFromSortKey} from 'search/SortUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import ExpandedScrollableTable, {
  calcExpandedLeftPadding,
} from 'ui-library/scrollable/ExpandedScrollableTable';
import {ScrollableTableUISelector} from 'ui-library/scrollable/reduceScrollableTableUI';
import {ScrollableTable} from 'ui-library/scrollable/ScrollableTable';
import {
  RowSelectionMode,
  ScrollableTableColumn,
  ScrollableTableRow,
  TABLE_ROW_BODY_HEIGHT,
} from 'ui-library/scrollable/ScrollableTableConstants';
import {cycleColumnHeaderSort, getColumnHeaderSortDirection} from 'ui-library/scrollable/ScrollableTableUtil';
import preserveShallowEquality from 'util/preserveShallowEquality';

export interface AlignmentViewProps {
  items: Array<ListableItem>;
  emptyMessage: string;
  sorts: Array<Sort>;
  tableKey: string;
  fallbackTableKey?: string;
  isCompact: boolean;
  selectedIds: Array<string>;
  onSetColumnHeaderSort: (sort: Sort) => void;
  zoom: number;
  onToggleSelectedIds: (ids: Array<string>, isSelected: boolean) => void;
  onOpenItem: (item: ListableItem) => void;
  comparing: boolean;
  finderId: string;
  collection?: string | null;
  searchSection: NavConstants.Section;
  blastOperator?: Operator;
  coloringScheme: ColoringSchemeType;
}

export interface EnhancedProps {
  bioentityAliasInfosByBioentityId: {[bioentityId: string]: Array<BioentityAliasInfo> | undefined};
}

export interface AlignmentViewRow extends ScrollableTableRow {
  extra: {item?: BioentitySummary};
  rowHspId: string;
}

const CLEAR_POINT = {x: -1, y: -1};

export const DEFAULT_COLUMN_WIDTH: Partial<Record<ColumnAccessorKey, number>> = {
  [ColumnAccessorKey.ID]: 130,
  [ColumnAccessorKey.NAME]: 130,
  [ColumnAccessorKey.HIT_RANGE]: 130,
  [ColumnAccessorKey.PERCENT_IDENTITY]: 100,
  [ColumnAccessorKey.E_VALUE]: 100,
};

const getSequenceColumnWidth = (
  tableKey: string,
  isRegistry: boolean,
  hiddenColumnKeys: Array<string>,
  tableWidth: number
): number => {
  const attributeColumnsWidth = Object.entries(DEFAULT_COLUMN_WIDTH)
    .filter(
      ([accessorKey]) =>
        (isRegistry ? accessorKey !== ColumnAccessorKey.NAME : accessorKey !== ColumnAccessorKey.ID) &&
        !hiddenColumnKeys.includes(accessorKey)
    )
    .map(
      ([accessorKey, defaultWidth]) =>
        preferences.get(ScrollableTable.getColumnWidthPreferenceKey(tableKey, accessorKey)) || defaultWidth
    )
    .reduce((width, sum) => width + sum, 0);
  return Math.max(
    0,
    tableWidth -
      attributeColumnsWidth -
      ALIGNMENT_TABLE_SCROLLBAR_BUFFER -
      calcExpandedLeftPadding(tableWidth)
  );
};

const getSequenceWidth = (sequenceColumnWidth: number): number =>
  Math.max(0, sequenceColumnWidth - SEQUENCE_COLUMN_PADDING);

export function AlignmentView(props: AlignmentViewProps & EnhancedProps): JSX.Element {
  // This reference is used to access the underlying FixedDataTable APIs.
  const tableRef = React.useRef<Table>(null);
  const scrollableTableUiSelector = React.useRef(new ScrollableTableUISelector(getStore().getState()));
  const [sequenceColumnScroll, setSequenceColumnScroll] = React.useState(0);
  const [customScroll, setCustomScroll] = React.useState(0);
  const [verticalScroll, setVerticalScroll] = React.useState(0);

  const [dragWindowStart, setDragWindowStart] = React.useState(CLEAR_POINT);
  const [dragWindowEnd, setDragWindowEnd] = React.useState(CLEAR_POINT);
  const [alignmentSelect, setAlignmentSelect] = React.useState<AlignmentSelect | null>(null);

  // Table size is determined by the AutoSizer in ScrollableTable.
  const [tableSize, setTableSize] = React.useState({height: 0, width: 0});
  // The window size is usually 56px wider than the table size.
  // The extra pixel comes from the navbar (54px) and probably borderline (2px).
  const [windowSize, setWindowSize] = React.useState({height: 0, width: 0});

  const [sequenceColumnWidth, setSequenceColumnWidth] = React.useState(
    getSequenceColumnWidth(
      props.tableKey,
      props.finderId === REGISTRIES_FINDER_ID,
      scrollableTableUiSelector.current.getHiddenColumnKeysForTable(
        props.tableKey,
        null,
        props.fallbackTableKey
      ),
      tableSize.width
    )
  );
  const queryLength =
    props.items?.[0]?.attributeValues?.query_alignment_sequence?.length ||
    getSequenceWidth(sequenceColumnWidth);

  // By default, use flexGrow = 0, so we can accurately calculate the attribute column widths.
  // When the sequence column is too narrow, we will omit the sequence column, and set flexGrow = 1
  // to fill up the remaining space.
  const flexGrow = sequenceColumnWidth > SEQUENCE_COLUMN_PADDING ? 0 : 1;

  useEffect(() => {
    const handleResize = (): void => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
      setSequenceColumnWidth(
        getSequenceColumnWidth(
          props.tableKey,
          props.finderId === REGISTRIES_FINDER_ID,
          scrollableTableUiSelector.current.getHiddenColumnKeysForTable(
            props.tableKey,
            null,
            props.fallbackTableKey
          ),
          tableSize.width
        )
      );
    };

    window.addEventListener('resize', handleResize);
    handleResize();
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [props.finderId, props.tableKey, props.fallbackTableKey, scrollableTableUiSelector, tableSize.width]);

  useEffect(() => {
    if (props.zoom === 0) {
      setSequenceColumnScroll(0);
    } else {
      const alignments = alignmentSelect;

      if (alignments) {
        const newScroll = adjustScrollForZoomChange(
          alignments.residueStartX,
          props.zoom,
          getSequenceWidth(sequenceColumnWidth),
          queryLength,
          alignments.residueEndX
        );
        setSequenceColumnScroll(newScroll);
        setCustomScroll(newScroll);
      }
    }
    // exclude alignmentSelect from the dependency array to prevent infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.searchSection, props.zoom, queryLength, setSequenceColumnScroll, sequenceColumnWidth]);

  if (!props.items.length) {
    return (
      <div
        className="sidepanel-fadedMessage expandedBrowser-emptyMessage"
        {...getTestProps({component: 'AlignmentView', element: 'emptyMessage'})}
      >
        {props.emptyMessage}
      </div>
    );
  }

  const handleDragStart = (e: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
    e.preventDefault();
    // Bound the value of adjX allows users to select the start and end of the alignment sequence
    // even when clicking outside the alignment boundaries. Without this bound, it can be difficult
    // to select the start or end of the alignment.
    const adjX = boundMinMax(
      0,
      getAdjustedX(e.pageX, windowSize.width, sequenceColumnWidth),
      getSequenceWidth(sequenceColumnWidth)
    );
    const adjY = getAdjustedY(e.pageY, windowSize.height, tableSize.height);

    if (insideAlignmentColumn(adjX, adjY, props.isCompact, tableSize.height)) {
      const totalComponentSize = getTotalComponentSize(
        props.zoom,
        getSequenceWidth(sequenceColumnWidth),
        queryLength
      );
      let newResidueX;

      if (props.zoom >= PAIRWISE_ALIGNMENT_ZOOM_THRESHOLD) {
        const roundedPixelStartX = roundToNearestMultiple(
          adjX + sequenceColumnScroll,
          zoomToPairwiseItemSize(props.zoom)
        );
        newResidueX = (roundedPixelStartX / totalComponentSize) * queryLength;
        setDragWindowStart({x: roundedPixelStartX, y: adjY + verticalScroll});
      } else {
        const pixelStartX = adjX + sequenceColumnScroll;
        newResidueX = (pixelStartX / totalComponentSize) * queryLength;
        setDragWindowStart({x: pixelStartX, y: adjY + verticalScroll});
      }

      if (e.shiftKey) {
        if (alignmentSelect && alignmentSelect.residueStartX >= 0) {
          setEndSelect(
            setAlignmentSelect,
            newResidueX,
            adjY + verticalScroll,
            adjY + verticalScroll,
            rows,
            getNormalRowHeight(props.isCompact),
            getQueryRowHeight(props.isCompact)
          );
          return;
        }
      }

      setDragWindowEnd({x: -1, y: -1});
      setStartSelect(setAlignmentSelect, newResidueX);
    }
  };

  const handleClickRow = (row: AlignmentViewRow, e: React.MouseEvent<Table, MouseEvent>): void => {
    const adjX = getAdjustedX(e.pageX, windowSize.width, sequenceColumnWidth);
    const adjY = getAdjustedY(e.pageY, windowSize.height, tableSize.height);

    if (insideAlignmentColumn(adjX, adjY, props.isCompact, tableSize.height)) {
      // opening item should only work outside the alignment column
      return;
    }

    if (row.extra.item) {
      props.onOpenItem(row.extra.item as ListableItem);
    }
  };

  const handleToggleSelectResults = (rows: Array<ScrollableTableRow>, isSelected: boolean): void => {
    const selectedIds = Array.from(new Set(rows.map((row) => row.id)));
    props.onToggleSelectedIds(selectedIds, isSelected);
  };

  const handleChangeSort = (newSortKey: string): void => {
    const newSort = cycleColumnHeaderSort(props.sorts, newSortKey);
    props.onSetColumnHeaderSort(newSort);
  };

  const [columns, rows] = getColumnsAndRows(
    sequenceColumnScroll,
    getSequenceWidth(sequenceColumnWidth),
    sequenceColumnWidth,
    handleDragStart,
    props,
    alignmentSelect,
    props.blastOperator,
    props.coloringScheme,
    flexGrow
  );

  return (
    <div className={classNames('u-flex-display', 'u-flex-grow')}>
      {sequenceColumnWidth > SEQUENCE_COLUMN_PADDING && (
        <ScrollbarAndSelectHandler
          scroll={sequenceColumnScroll}
          setScroll={setSequenceColumnScroll}
          customScroll={customScroll}
          setCustomScroll={setCustomScroll}
          sequenceWidth={getSequenceWidth(sequenceColumnWidth)}
          scrollableAreaLength={sequenceColumnWidth}
          scrollableAreaHeight={tableSize.height}
          handleDragStart={handleDragStart}
          dragWindowStart={dragWindowStart}
          dragWindowEnd={dragWindowEnd}
          setDragWindowEnd={setDragWindowEnd}
          alignmentSelect={alignmentSelect}
          setAlignmentSelect={setAlignmentSelect}
          queryRowHeight={getQueryRowHeight(props.isCompact)}
          normalRowHeight={getNormalRowHeight(props.isCompact)}
          zoom={props.zoom}
          tableSize={tableSize}
          windowSize={windowSize}
          queryLength={queryLength}
          verticalScroll={verticalScroll}
          rows={rows}
        />
      )}
      <ExpandedScrollableTable
        canFilterColumns={true}
        canResizeColumns={true}
        rowSelectionMode={RowSelectionMode.MULTIPLE}
        className="u-flex-grow"
        columns={columns}
        getRowContentHeight={(rowIndex: number) =>
          rowIndex === 0 ? QUERY_ROW_CONTENT_HEIGHT : TABLE_ROW_BODY_HEIGHT
        }
        isCompact={props.isCompact}
        matchParentHeight={true}
        onRowClick={handleClickRow}
        onRowsSelect={handleToggleSelectResults}
        rows={rows}
        tableKey={props.tableKey}
        fallbackTableKey={props.fallbackTableKey}
        bufferRowCount={0}
        fixedRowCount={1}
        onResize={(newSize) => {
          setTableSize(newSize);
          setSequenceColumnWidth(
            getSequenceColumnWidth(
              props.tableKey,
              props.finderId === REGISTRIES_FINDER_ID,
              scrollableTableUiSelector.current.getHiddenColumnKeysForTable(
                props.tableKey,
                null,
                props.fallbackTableKey
              ),
              newSize.width
            )
          );
        }}
        onScrollEnd={(x: number, y: number) => {
          setVerticalScroll(y);
        }}
        onChangeSort={handleChangeSort}
        allowOverflow={true}
        onColumnResizeEndCallback={() => {
          setSequenceColumnWidth(
            getSequenceColumnWidth(
              props.tableKey,
              props.finderId === REGISTRIES_FINDER_ID,
              scrollableTableUiSelector.current.getHiddenColumnKeysForTable(
                props.tableKey,
                null,
                props.fallbackTableKey
              ),
              tableSize.width
            )
          );
        }}
        onFilterColumns={(hiddenColumnKeys) => {
          setSequenceColumnWidth(
            getSequenceColumnWidth(
              props.tableKey,
              props.finderId === REGISTRIES_FINDER_ID,
              hiddenColumnKeys,
              tableSize.width
            )
          );
        }}
        tableRef={tableRef}
      />
    </div>
  );
}

interface RowDataById {
  [itemId: string]: {[accessorKey: string]: unknown};
}

interface IconsById {
  [itemId: string]: JSX.Element;
}

function getQueryAlignmentSequence(
  item?: BioentitySummary & Partial<ListableItemExtras>
): string | undefined {
  return item?.attributeValues?.query_alignment_sequence;
}

function getColumnsAndRows(
  scroll: number,
  sequenceWidth: number,
  sequenceColumnWidth: number,
  handleDragStart: (e: React.MouseEvent<HTMLDivElement>) => void,
  props: AlignmentViewProps & EnhancedProps,
  alignmentSelect: AlignmentSelect | null,
  blastOperator: Operator | undefined,
  coloringScheme: ColoringSchemeType,
  flexGrow: number
): [Array<ScrollableTableColumn>, Array<AlignmentViewRow>] {
  const items = props.items as Array<BioentitySummary & Partial<ListableItemExtras>>;
  const isRegistry = props.finderId === REGISTRIES_FINDER_ID;
  type ListableFolderItem = ListingTypes[ListingRendererType.FOLDER_ITEM];
  const columnConfigurations = _.compact([
    // display ID column if registry, otherwise display name column
    isRegistry &&
      ({
        column: {
          header: getColumnHeaderFromSortKey(SortKey.SORT_REGISTRY_IDENTIFIER_KEY),
          accessorKey: ColumnAccessorKey.ID,
          flexGrow,
          isCollapsible: false,
          isSortable: true,
          sortKey: SortKey.SORT_REGISTRY_IDENTIFIER_KEY,
          width: DEFAULT_COLUMN_WIDTH[ColumnAccessorKey.NAME],
        },
        render: (item: ListableFolderItem) =>
          getIdColumnRender(item, props.bioentityAliasInfosByBioentityId, isRegistry, props.collection),
      } as ColumnConfiguration<ListableFolderItem>),
    !isRegistry &&
      ({
        column: {
          header: getColumnHeaderFromSortKey(SortKey.SORT_NAME_KEY),
          accessorKey: ColumnAccessorKey.NAME,
          flexGrow,
          isCollapsible: false,
          isSortable: true,
          sortKey: SortKey.SORT_NAME_KEY,
          width: DEFAULT_COLUMN_WIDTH[ColumnAccessorKey.NAME],
        },
        render: (item: ListableFolderItem) =>
          getNameColumnRender(item, props.bioentityAliasInfosByBioentityId, isRegistry, props.collection),
      } satisfies ColumnConfiguration<ListableFolderItem>),
    // Each hit can have multiple HSPs, thus multiple rows in the table.
    // So the rendering is done in getHitRows for the following columns.
    {
      column: {
        header: 'Hit range',
        accessorKey: ColumnAccessorKey.HIT_RANGE,
        flexGrow,
        isCollapsible: false,
        isSortable: false,
        width: DEFAULT_COLUMN_WIDTH[ColumnAccessorKey.HIT_RANGE],
      },
      render: () => <span />,
    } satisfies ColumnConfiguration<ListableFolderItem>,
    {
      column: {
        header: 'Percent identity',
        accessorKey: ColumnAccessorKey.PERCENT_IDENTITY,
        flexGrow,
        isCollapsible: false,
        isSortable: false,
        width: DEFAULT_COLUMN_WIDTH[ColumnAccessorKey.PERCENT_IDENTITY],
      },
      render: () => <span />,
    } satisfies ColumnConfiguration<ListableFolderItem>,
    {
      column: {
        header: getColumnHeaderFromSortKey(SortKey.SORT_BLAST_E_VALUE),
        accessorKey: ColumnAccessorKey.E_VALUE,
        flexGrow,
        isCollapsible: false,
        isSortable: false,
        width: DEFAULT_COLUMN_WIDTH[ColumnAccessorKey.E_VALUE],
      },
      render: () => <span />,
    } satisfies ColumnConfiguration<ListableFolderItem>,
  ]);
  if (sequenceColumnWidth > SEQUENCE_COLUMN_PADDING) {
    columnConfigurations.push({
      column: {
        header: 'Sequence',
        accessorKey: ColumnAccessorKey.SEQUENCE,
        width: sequenceColumnWidth,
        flexGrow,
        isFixedRight: true,
        isCollapsible: false,
        isSortable: false,
        disableFiltering: true,
      },
      render: () => <span />,
    } satisfies ColumnConfiguration<ListableFolderItem>);
  }

  const columns = columnConfigurations.map(
    (columnConfiguration: ColumnConfiguration<ListingTypes[ListingRendererType.FOLDER_ITEM]>) => ({
      ...columnConfiguration.column,
      sortDirection: columnConfiguration.column.isSortable
        ? getColumnHeaderSortDirection(props.sorts, columnConfiguration.column.sortKey!)
        : null,
    })
  );

  const rowDataByItemId: RowDataById = {};
  const iconsByItemId: IconsById = {};
  const listingRenderer = new FolderItemListingRenderer();
  for (const item of items) {
    rowDataByItemId[item.id] = {};
    for (const columnConfiguration of columnConfigurations) {
      rowDataByItemId[item.id][columnConfiguration.column.accessorKey] = columnConfiguration.render(item);
    }
    iconsByItemId[item.id] = listingRenderer.renderIcon(item, {shouldShowShortcuts: false});
  }

  const rows: Array<AlignmentViewRow> = [];
  rows.push(
    getQueryRow(
      scroll,
      props.zoom,
      items[0],
      handleDragStart,
      getQueryRowHeight(props.isCompact),
      sequenceWidth,
      props.isCompact,
      alignmentSelect,
      blastOperator,
      coloringScheme
    )
  );

  for (const item of items) {
    if (props.comparing && !props.selectedIds.includes(item.id)) {
      continue;
    }

    // the query_alignment_sequence only exists in one of the items per api_identifier
    const itemWithQueryAlignmentSequence = items.find(
      (_item) => getQueryAlignmentSequence(_item) !== undefined
    );
    const featureStatus = getFeatureStatus({
      feature: StandardFeature.OPEN,
      item,
      itemState: {isRegistered: FileUtil.isInRegistry(item)},
    });

    rows.push(
      ...getHitRows(
        scroll,
        props.zoom,
        item,
        getQueryAlignmentSequence(itemWithQueryAlignmentSequence) || '',
        rowDataByItemId[item.id],
        iconsByItemId[item.id],
        props.selectedIds.includes(item.id),
        featureStatus,
        handleDragStart,
        getNormalRowHeight(props.isCompact),
        sequenceWidth,
        props.isCompact,
        alignmentSelect,
        blastOperator,
        coloringScheme
      )
    );
  }

  return [columns, rows];
}

function getQueryRow(
  scroll: number,
  zoom: number,
  item: BioentitySummary & Partial<ListableItemExtras>,
  handleDragStart: (e: React.MouseEvent<HTMLDivElement>) => void,
  queryRowHeight: number,
  queryRowWidth: number,
  isCompact: boolean,
  alignmentSelect: AlignmentSelect | null,
  blastOperator: Operator | undefined,
  coloringScheme: ColoringSchemeType
): AlignmentViewRow {
  const sequence = getQueryAlignmentSequence(item) || '';
  const selectedAlignments = alignmentSelect;
  const alignment =
    zoom < PAIRWISE_ALIGNMENT_ZOOM_THRESHOLD ? (
      <GraphAlignment
        queryLength={sequence.length}
        hitStart={1}
        hitEnd={sequence.length}
        zoomLevel={zoom}
        alignmentScore={0}
        scroll={scroll}
        rowHeight={queryRowHeight}
        rowWidth={queryRowWidth}
        isCompact={isCompact}
        handleDragStart={handleDragStart}
        dragSelected={selectedAlignments ? arrangeStartAndEnd(selectedAlignments, '') : undefined}
        isQuery={true}
        enableTooltip={false}
        showRuler={true}
      />
    ) : (
      <PairwiseAlignment
        scroll={scroll}
        zoom={zoom}
        rowHeight={queryRowHeight}
        rowWidth={queryRowWidth}
        isCompact={isCompact}
        handleDragStart={handleDragStart}
        dragSelected={selectedAlignments ? arrangeStartAndEnd(selectedAlignments, '') : undefined}
        querySequence={sequence.toUpperCase()}
        blastOperator={blastOperator}
        coloringScheme={coloringScheme}
      />
    );

  return {
    id: 'query',
    rowHspId: 'query',
    isFaded: false,
    data: {
      [ColumnAccessorKey.ID]: 'QUERY',
      [ColumnAccessorKey.NAME]: 'Query',
      [ColumnAccessorKey.HIT_RANGE]: <SequenceRangeCell start={1} end={sequence.length} />,
      [ColumnAccessorKey.PERCENT_IDENTITY]: '',
      [ColumnAccessorKey.E_VALUE]: '',
      [ColumnAccessorKey.SEQUENCE]: alignment,
    },
    icon: <BenchlingIcon className="scrollableTable-cell-icon" icon="blast" />,
    extra: {},
    isExpandable: false,
    isExpanded: false,
    isSelected: false,
    tooltipText: undefined,
    isSelectorPresent: false,
  } as AlignmentViewRow;
}

interface HitResult {
  hsps: Array<HitHsp>;
  query_alignment_sequence?: string;
}

interface ContextInterface {}

function getHitRows(
  scroll: number,
  zoom: number,
  item: BioentitySummary & Partial<ListableItemExtras>,
  queryAlignmentSequence: string,
  itemRowData: {[accessorKey: string]: unknown},
  icon: JSX.Element,
  isSelected: boolean,
  featureStatus: FeatureStatus,
  handleDragStart: (e: React.MouseEvent<HTMLDivElement>) => void,
  hitRowHeight: number,
  hitRowWidth: number,
  isCompact: boolean,
  alignmentSelect: AlignmentSelect | null,
  blastOperator: Operator | undefined,
  coloringScheme: ColoringSchemeType
): Array<AlignmentViewRow> {
  const rows: Array<AlignmentViewRow> = [];
  const hitResult = item.attributeValues ? (item.attributeValues as HitResult) : undefined;

  const selectedAlignments = alignmentSelect;

  for (const hsp of hitResult?.hsps || []) {
    const alignment = (
      <Context.Consumer>
        {(contextValue: ContextInterface) =>
          zoom < PAIRWISE_ALIGNMENT_ZOOM_THRESHOLD ? (
            <GraphAlignment
              queryLength={queryAlignmentSequence.length}
              hitStart={hsp.query_from}
              hitEnd={hsp.query_to}
              zoomLevel={zoom}
              alignmentScore={hsp.score}
              rowHeight={hitRowHeight}
              rowWidth={hitRowWidth}
              isCompact={isCompact}
              handleDragStart={handleDragStart}
              dragSelected={
                selectedAlignments
                  ? arrangeStartAndEnd(selectedAlignments, `${item.id}-${hsp.num}`)
                  : undefined
              }
              scroll={scroll}
              enableTooltip={true}
            />
          ) : (
            <PairwiseAlignment
              scroll={scroll}
              zoom={zoom}
              rowHeight={hitRowHeight}
              rowWidth={hitRowWidth}
              isCompact={isCompact}
              handleDragStart={handleDragStart}
              dragSelected={
                selectedAlignments
                  ? arrangeStartAndEnd(selectedAlignments, `${item.id}-${hsp.num}`)
                  : undefined
              }
              querySequence={queryAlignmentSequence.toUpperCase()}
              hitSequence={{
                start: (hsp.alignment_query_from ?? hsp.query_from) - 1,
                end: (hsp.alignment_query_to ?? hsp.query_to) - 1,
                sequence: hsp.hseq,
                diffs: hsp.hseq_diffs,
              }}
              blastOperator={blastOperator}
              coloringScheme={coloringScheme}
            />
          )
        }
      </Context.Consumer>
    );

    rows.push({
      id: item.id,
      rowHspId: `${item.id}-${hsp.num}`,
      isFaded: Boolean(item.archiveRecord) || !featureStatus.enabled,
      data: {
        [ColumnAccessorKey.ID]: hsp.num === 1 ? itemRowData[ColumnAccessorKey.ID] : undefined,
        [ColumnAccessorKey.NAME]: hsp.num === 1 ? itemRowData[ColumnAccessorKey.NAME] : undefined,
        [ColumnAccessorKey.HIT_RANGE]: <SequenceRangeCell start={hsp.query_from} end={hsp.query_to} />,
        [ColumnAccessorKey.PERCENT_IDENTITY]: `${((hsp.identity / hsp.align_len) * 100).toFixed(1)}%`,
        [ColumnAccessorKey.E_VALUE]: hsp.evalue.toExponential(2),
        [ColumnAccessorKey.SEQUENCE]: alignment,
      },
      icon,
      extra: {item},
      isExpandable: false,
      isExpanded: false,
      isSelectorDisabled: hsp.num !== 1,
      isSelected,
      tooltipText: featureStatus.enabled ? undefined : featureStatus.userMessage,
    });
  }
  return rows;
}

class AlignmentViewConnector extends DataConnector<AlignmentViewProps, EnhancedProps> {
  override stores = [AssayStore, InventoryFileStore, SampleSchemaStore, StorableStore];

  enhanceProps: () => EnhancedProps;

  constructor(props: AlignmentViewProps) {
    super(props);
    this.enhanceProps = preserveShallowEquality(this.uncachedEnhanceProps);
  }

  uncachedEnhanceProps = (): EnhancedProps => {
    const bioentityIds = this.props.items.flatMap((item) =>
      FileUtil.isInventoryFile(item) ? [item.id] : []
    );
    const bioentityAliasInfosByBioentityId = this.getOrFetchMultipleOptionalDeprecated(
      ManagedModelType.BIOENTITY_ALIAS_INFOS_BY_BIOENTITY_ID,
      bioentityIds
    );
    return {
      bioentityAliasInfosByBioentityId,
    };
  };
}

const AlignmentViewInnerComponent = AlignmentViewConnector.connect(AlignmentView);

export default function AlignmentViewWrapper(props: AlignmentViewProps): JSX.Element {
  return <AlignmentViewInnerComponent {...props} />;
}
